import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import "./ConditionCard.css";
import { RULE_OPTIONS, getRulesForAttributeType } from "./conditions";
import {
  RuleType,
  PersonaCondition,
  AttributeConfig,
  AttributeConfigData,
  Employee,
  FlattenedEmployeeRecord,
  CustomProperty,
  CustomPropertyType,
} from "winslow-core";
import {
  NumberInput,
  DateRangeInput,
  CheckboxSingleInput,
  CheckboxMultipleInput,
  RadioInput,
  DropdownInput,
  TextInputWithSuggestions,
} from "./ConditionValueInputs";
import {
  getCustomPropertyForAttribute,
  getPropertyChoices,
  isEditableDropdown,
} from "./propertyHelpers";

export interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { id: string; label: string; category?: string }[];
  disabled?: boolean;
}

const SelectField: React.FC<SelectFieldProps> = ({
  label,
  value,
  onChange,
  options,
  disabled = false,
}) => {
  // Group options by category for better organization
  const groupedOptions = options.reduce(
    (acc, option) => {
      const category = option.category || "other";
      if (!acc[category]) acc[category] = [];
      acc[category].push(option);
      return acc;
    },
    {} as Record<string, typeof options>
  );

  return (
    <div className="condition-card-select-wrapper">
      <label className="condition-card-select-label">{label}</label>
      <div className="condition-card-select-container">
        <select
          className="condition-card-select-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          aria-label={label}>
          <option value="">Select</option>
          {Object.entries(groupedOptions).map(([category, categoryOptions]) => (
            <optgroup
              key={category}
              label={category.charAt(0).toUpperCase() + category.slice(1)}>
              {categoryOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
    </div>
  );
};

export interface AttributeSelectProps {
  label: string;
  value: string;
  onChange: (attributePath: string) => void;
  attributeConfig: AttributeConfigData;
  allowedCategories?: string[];
  getAvailableAttributePathsFunc?: () => string[];
}

const AttributeSelect: React.FC<AttributeSelectProps> = ({
  label,
  value,
  onChange,
  attributeConfig,
  allowedCategories,
  getAvailableAttributePathsFunc,
}) => {
  const allAttributeOptions = useMemo(() => {
    const options: {
      id: string;
      label: string;
      category: string;
      hasValues: boolean;
    }[] = [];
    const availablePaths = getAvailableAttributePathsFunc
      ? getAvailableAttributePathsFunc()
      : [];

    Object.entries(attributeConfig).forEach(([category, attributes]) => {
      if (allowedCategories && !allowedCategories.includes(category)) {
        return;
      }

      const categoryLabel =
        category.charAt(0).toUpperCase() +
        category.slice(1).replace(/([A-Z])/g, " $1");

      attributes.forEach((attr: any) => {
        const hasValues = availablePaths.includes(attr.path);
        options.push({
          id: attr.path,
          label: `${attr.displayName}${!hasValues ? " (no values)" : ""}`,
          category: categoryLabel,
          hasValues,
        });
      });
    });

    return options.sort((a, b) => {
      if (a.category !== b.category) {
        return a.category.localeCompare(b.category);
      }
      return a.label.localeCompare(b.label);
    });
  }, [attributeConfig, allowedCategories, getAvailableAttributePathsFunc]);

  return (
    <div className="condition-card-select-wrapper">
      <label className="condition-card-select-label">{label}</label>
      <div className="condition-card-select-container">
        <select
          className="condition-card-select-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}>
          <option value="">Select Attribute</option>
          {allAttributeOptions.map((option) => (
            <option
              key={option.id}
              value={option.hasValues ? option.id : ""}
              disabled={!option.hasValues}
              className={option.hasValues ? "" : "no-values"}
              style={{
                color: option.hasValues ? "inherit" : "#999",
                fontStyle: option.hasValues ? "normal" : "italic",
              }}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export interface ConditionCardProps {
  index: number;
  condition: PersonaCondition;
  onChange: (condition: PersonaCondition) => void;
  onDelete: (index: number) => void;
  attributeConfig?: AttributeConfigData;
  employees: Employee[];
  employeeRecords?: FlattenedEmployeeRecord[];
  customProperties?: CustomProperty[];
  useNativeHRIS?: boolean;
  allowedCategories?: string[];
  getSuggestionsFunc?: (attributePath: string, searchTerm: string) => string[];
  getAvailableAttributePathsFunc?: () => string[];
  getAttributeConfigFunc?: () => AttributeConfigData;
}

export const ConditionCard: React.FC<ConditionCardProps> = ({
  index,
  condition,
  onChange,
  onDelete,
  attributeConfig,
  employees,
  employeeRecords = [],
  customProperties = [],
  useNativeHRIS = false,
  allowedCategories,
  getSuggestionsFunc,
  getAvailableAttributePathsFunc,
  getAttributeConfigFunc,
}) => {
  const [attributePath, setAttributePath] = useState<string>(
    condition.attributePath || ""
  );
  const [rule, setRule] = useState<string>(condition.rule || "");
  const [value, setValue] = useState<string>(condition.value || "");
  const [displayName, setDisplayName] = useState<string>(
    condition.displayName || ""
  );
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const completeAttributeConfig = useMemo(() => {
    if (attributeConfig) {
      return attributeConfig;
    }

    if (getAttributeConfigFunc) {
      return getAttributeConfigFunc();
    }

    return AttributeConfig.getAvailableAttributes();
  }, [attributeConfig, getAttributeConfigFunc]);

  // Use refs to track previous condition values to prevent unnecessary updates
  const prevCondition = useRef<PersonaCondition>(condition);

  useEffect(() => {
    const prev = prevCondition.current;
    let hasChanged = false;

    if (condition.attributePath !== prev.attributePath) {
      setAttributePath(condition.attributePath || "");
      hasChanged = true;
    }
    if (condition.rule !== prev.rule) {
      setRule(condition.rule || "");
      hasChanged = true;
    }
    if (condition.value !== prev.value) {
      setValue(condition.value || "");
      hasChanged = true;
    }
    if (condition.displayName !== prev.displayName) {
      setDisplayName(condition.displayName || "");
      hasChanged = true;
    }

    if (hasChanged) {
      prevCondition.current = condition;
    }
  }, [condition]);

  // Update display name when attribute path changes
  useEffect(() => {
    if (attributePath && completeAttributeConfig) {
      for (const attributes of Object.values(completeAttributeConfig)) {
        const foundAttr = attributes.find(
          (attr: any) => attr.path === attributePath
        );
        if (foundAttr && foundAttr.displayName !== displayName) {
          setDisplayName(foundAttr.displayName);
          break;
        }
      }
    }
  }, [attributePath, completeAttributeConfig, displayName]);

  // Memoize the condition object to prevent unnecessary re-renders
  const currentCondition = useMemo(
    () => ({
      attributePath,
      rule: rule as RuleType,
      value,
      displayName: displayName || undefined,
    }),
    [attributePath, rule, value, displayName]
  );

  // Use useRef to track the last sent condition to prevent loops
  const lastSentCondition = useRef<PersonaCondition | null>(null);

  useEffect(() => {
    // Only call onChange if the condition actually changed
    if (
      !lastSentCondition.current ||
      lastSentCondition.current.attributePath !==
        currentCondition.attributePath ||
      lastSentCondition.current.rule !== currentCondition.rule ||
      lastSentCondition.current.value !== currentCondition.value ||
      lastSentCondition.current.displayName !== currentCondition.displayName
    ) {
      lastSentCondition.current = currentCondition;
      onChange(currentCondition);
    }
  }, [currentCondition, onChange]);

  const fetchSuggestions = useCallback(
    (attributePath: string, searchTerm: string = "") => {
      if (!attributePath) {
        console.warn("Cannot fetch suggestions: missing attributePath");
        return;
      }

      setLoadingSuggestions(true);
      try {
        const data = getSuggestionsFunc
          ? getSuggestionsFunc(attributePath, searchTerm)
          : [];
        setSuggestions(data || []);
      } catch (error) {
        console.error("Error getting suggestions:", error);
        setSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    },
    [getSuggestionsFunc]
  );

  // Debounced suggestion fetching with proper cleanup
  const timeoutRef = useRef<NodeJS.Timeout>();

  const debouncedFetchSuggestions = useCallback(
    (attributePath: string, searchTerm: string) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        fetchSuggestions(attributePath, searchTerm);
      }, 300);
    },
    [fetchSuggestions]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Fetch suggestions when attribute path changes or when input is focused
  useEffect(() => {
    if (showSuggestions && attributePath) {
      debouncedFetchSuggestions(attributePath, value);
    }
  }, [showSuggestions, attributePath, value, debouncedFetchSuggestions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Get property type for the selected attribute
  const getPropertyTypeForAttribute = useCallback(
    (attributePath: string): CustomPropertyType | "employeeType" | null => {
      if (attributePath === "employeeType") {
        return "employeeType";
      }
      const property = getCustomPropertyForAttribute(
        attributePath,
        customProperties
      );
      return property?.type || null;
    },
    [customProperties]
  );

  const currentPropertyType = useMemo(
    () => getPropertyTypeForAttribute(attributePath),
    [attributePath, getPropertyTypeForAttribute]
  );

  // Get available rules based on property type
  const availableRules = useMemo(() => {
    if (currentPropertyType) {
      return getRulesForAttributeType(currentPropertyType as any);
    }
    return RULE_OPTIONS;
  }, [currentPropertyType]);

  const handleAttributeChange = (newAttributePath: string) => {
    setAttributePath(newAttributePath);
    // Reset value when attribute changes
    setValue("");
    // Reset suggestions when attribute changes
    setSuggestions([]);
    setShowSuggestions(false);
    
    // Auto-select rule if only 1 available for the new attribute
    const newPropertyType = getPropertyTypeForAttribute(newAttributePath);
    const newAvailableRules = newPropertyType
      ? getRulesForAttributeType(newPropertyType as any)
      : RULE_OPTIONS;
    
    if (newAvailableRules.length === 1) {
      setRule(newAvailableRules[0].id);
    } else {
      setRule("");
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setValue(suggestion);
    setShowSuggestions(false);
  };

  const handleValueChange = (newValue: string) => {
    setValue(newValue);
    if (newValue.trim() && attributePath) {
      debouncedFetchSuggestions(attributePath, newValue);
    }
  };

  const handleValueFocus = () => {
    setShowSuggestions(true);
    if (attributePath && !suggestions.length && !loadingSuggestions) {
      fetchSuggestions(attributePath, value);
    }
  };

  if (!completeAttributeConfig) {
    return (
      <div className="condition-card-container">
        <div className="condition-card-card">
          <div className="condition-card-content">
            <p>Loading available attributes...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="condition-card-container">
      <div className="condition-card-card">
        <div className="condition-card-content">
          <div className="condition-card-selection-row">
            <AttributeSelect
              label="Attribute"
              value={attributePath}
              onChange={handleAttributeChange}
              attributeConfig={completeAttributeConfig}
              allowedCategories={allowedCategories}
              getAvailableAttributePathsFunc={getAvailableAttributePathsFunc}
            />
            <SelectField
              label="Rule"
              value={rule}
              onChange={setRule}
              options={availableRules}
              disabled={availableRules.length === 1}
            />
          </div>
          <div className="condition-card-value-section">
            <label className="condition-card-value-label">Value</label>
            <div className="condition-card-value-container">
              {(() => {
                // Render appropriate input based on property type
                switch (currentPropertyType) {
                  case CustomPropertyType.NUMBER:
                    return <NumberInput value={value} onChange={setValue} />;

                  case CustomPropertyType.DATE:
                    return (
                      <DateRangeInput
                        value={value}
                        onChange={setValue}
                        rule={rule}
                      />
                    );

                  case CustomPropertyType.CHECKBOX_SINGLE:
                    return (
                      <CheckboxSingleInput value={value} onChange={setValue} />
                    );

                  case CustomPropertyType.CHECKBOX_MULTIPLE:
                    return (
                      <CheckboxMultipleInput
                        value={value}
                        onChange={setValue}
                        choices={getPropertyChoices(
                          attributePath,
                          customProperties
                        )}
                      />
                    );

                  case CustomPropertyType.RADIO:
                    return (
                      <RadioInput
                        value={value}
                        onChange={setValue}
                        choices={getPropertyChoices(
                          attributePath,
                          customProperties
                        )}
                      />
                    );

                  case CustomPropertyType.DROPDOWN:
                    return (
                      <DropdownInput
                        value={value}
                        onChange={setValue}
                        choices={getPropertyChoices(
                          attributePath,
                          customProperties
                        )}
                        editable={isEditableDropdown(
                          attributePath,
                          customProperties
                        )}
                        suggestions={suggestions}
                      />
                    );

                  case "employeeType":
                    return (
                      <RadioInput
                        value={value}
                        onChange={setValue}
                        choices={[
                          "US_FULL_TIME",
                          "US_PART_TIME",
                          "US_CONTRACTOR",
                          "US_VENDOR",
                          "INT_FULL_TIME",
                          "INT_CONTRACTOR",
                        ]}
                      />
                    );

                  default:
                    // Default to text input with suggestions for string types
                    return (
                      <TextInputWithSuggestions
                        value={value}
                        onChange={handleValueChange}
                        suggestions={suggestions}
                        loadingSuggestions={loadingSuggestions}
                        onFocus={handleValueFocus}
                      />
                    );
                }
              })()}
            </div>
          </div>
          <div
            className="condition-card-delete-button"
            aria-label="Delete condition">
            <span onClick={() => onDelete(index)}>Delete Condition</span>
          </div>
        </div>
      </div>
    </div>
  );
};
